/**
 * Corrections: what people changed before pressing send.
 *
 * Every row here was written by the send itself — Edit & send records the
 * draft/final pair in the same request that posts the reply, so this list is
 * complete by construction. Nothing on this page creates or edits anything;
 * it is the improvement loop's reading room.
 *
 * The diff is recomputed in the browser from the stored pair, with the same
 * word-diff the editor watched while typing. What is STORED is the raw texts;
 * a diff is a rendering, not a record.
 *
 * Two labels appear per row and they are different claims:
 *  - the editor's one-tap category (tone / missing fact / wrong policy / other),
 *    chosen at send time by the person who made the edit;
 *  - the nightly clustering job's failure mode, once that job exists (W4 step
 *    2). Until then every row shows "not clustered yet", which is the honest
 *    state, not a fault.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Layers, Lightbulb, PenLine, Play } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatRelative } from '../lib/format';
import { intentLabel } from '../lib/inbox/rows';
import { diffWords } from '../lib/ticket/diff';

interface Correction {
  id: string;
  ticketId: string;
  ticketRef: string;
  brand: string;
  brandSlug: string;
  intent: string | null;
  draft: string;
  final: string;
  humanCategory: string | null;
  failureMode: string | null;
  clusterKey: string | null;
  editedBy: string | null;
  createdAt: string;
}

interface Summary {
  total: number;
  lastWeek: number;
  unclustered: number;
  byCategory: Array<{ category: string | null; count: number }>;
  byBrand: Array<{ name: string; slug: string; count: number }>;
}

interface Cluster {
  clusterKey: string;
  failureMode: string | null;
  brand: string;
  brandSlug: string;
  intent: string | null;
  count: number;
  firstSeen: string;
  lastSeen: string;
  example: { ticketId: string; ticketRef: string; draft: string; final: string };
}

interface ClusterRunSummary {
  candidates: number;
  labelled: number;
  failed: number;
  newModes: string[];
  reason?: string;
}

/**
 * The same vocabulary the Edit & send panel offers, plus the filter for rows
 * where the editor skipped the tap — those are the rows clustering must label
 * without a human hint, which makes them worth finding, not hiding.
 */
const CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'tone', label: 'tone' },
  { key: 'missing_fact', label: 'missing fact' },
  { key: 'wrong_policy', label: 'wrong policy' },
  { key: 'other', label: 'other' },
  { key: 'none', label: 'uncategorised' },
];

const CATEGORY_TONE: Record<string, 'info' | 'held' | 'stop' | 'neutral'> = {
  tone: 'info',
  missing_fact: 'held',
  wrong_policy: 'stop',
  other: 'neutral',
};

function categoryLabel(key: string | null): string {
  if (key === null) return 'uncategorised';
  return CATEGORIES.find((c) => c.key === key)?.label ?? key.replace(/_/g, ' ');
}

export function CorrectionsPage() {
  // Filters live in the URL so a filtered view can be linked in a review
  // conversation — "look at wrong_policy for lifegauge" should be a paste.
  const [params, setParams] = useSearchParams();
  const brand = params.get('brand');
  const category = params.get('category');

  const setFilter = (key: 'brand' | 'category', value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const queryClient = useQueryClient();
  const [runOutcome, setRunOutcome] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['corrections', 'summary'],
    queryFn: () => apiGet<Summary>('/api/corrections/summary'),
  });
  const clusters = useQuery({
    queryKey: ['corrections', 'clusters'],
    queryFn: () => apiGet<Cluster[]>('/api/corrections/clusters'),
  });

  const runClustering = useMutation({
    mutationFn: () => apiPost<ClusterRunSummary>('/api/corrections/cluster'),
    onSuccess: async (result) => {
      setRunOutcome(
        result.reason ??
          `Labelled ${result.labelled} of ${result.candidates}` +
            (result.failed > 0 ? `, ${result.failed} failed` : '') +
            (result.newModes.length > 0
              ? `. New failure mode(s): ${result.newModes.join(', ')}.`
              : '.'),
      );
      await queryClient.invalidateQueries({ queryKey: ['corrections'] });
    },
    onError: (error: unknown) =>
      setRunOutcome(error instanceof ApiError ? error.message : 'The clustering pass did not run.'),
  });

  const query = new URLSearchParams();
  if (brand) query.set('brand', brand);
  if (category) query.set('category', category);
  const queryString = query.toString();
  const corrections = useQuery({
    queryKey: ['corrections', 'list', brand, category],
    queryFn: () =>
      apiGet<Correction[]>(`/api/corrections${queryString ? `?${queryString}` : ''}`),
  });

  if (corrections.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  const rows = corrections.data ?? [];
  const counts = new Map(
    (summary.data?.byCategory ?? []).map((g) => [g.category ?? 'none', g.count]),
  );

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-5">
      <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
        <Tile label="Corrections" value={String(summary.data?.total ?? 0)} note="all time" />
        <Tile label="Last 7 days" value={String(summary.data?.lastWeek ?? 0)} note="edit rate, roughly" />
        <Tile
          label="Awaiting clustering"
          value={String(summary.data?.unclustered ?? 0)}
          note="the nightly job's backlog"
        />
      </div>

      <Card className="mb-4">
        <CardHeader
          title="Clusters"
          meta="brand × intent × failure mode, biggest first"
          action={
            <Button size="sm" disabled={runClustering.isPending} onClick={() => runClustering.mutate()}>
              <Play size={13} strokeWidth={1.75} aria-hidden />
              {runClustering.isPending ? 'Labelling…' : 'Cluster now'}
            </Button>
          }
        />

        {runOutcome ? <p className="px-4 pt-3 text-xs text-ink-700">{runOutcome}</p> : null}

        {(clusters.data ?? []).length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Layers size={24} strokeWidth={1.5} className="mx-auto mb-2.5 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">Nothing clustered yet.</p>
            <p className="mx-auto mt-2 max-w-[540px] text-sm text-ink-400">
              The nightly pass labels each correction with a failure mode and groups them by brand,
              intent and mode — so nine tickets sharing one root cause surface as one cluster, not
              nine unrelated cases.{' '}
              {(summary.data?.unclustered ?? 0) > 0
                ? `${summary.data?.unclustered} correction(s) are waiting — press Cluster now to label them.`
                : 'It runs when there are corrections to label.'}
            </p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th aria-label="Expand" className="w-8" />
                <Th>Failure mode</Th>
                <Th>Brand</Th>
                <Th>Intent</Th>
                <Th className="text-right">Corrections</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {(clusters.data ?? []).map((cluster) => (
                <ClusterRow key={cluster.clusterKey} cluster={cluster} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="What people changed"
          meta={rows.length ? `${rows.length} newest${brand || category ? ', filtered' : ''}` : undefined}
        />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-2.5">
          <FilterRow
            label="Reason"
            options={CATEGORIES.map((c) => ({
              key: c.key,
              label: c.label,
              count: counts.get(c.key),
            }))}
            active={category}
            onSelect={(value) => setFilter('category', value)}
          />
          {(summary.data?.byBrand.length ?? 0) > 1 ? (
            <FilterRow
              label="Brand"
              options={(summary.data?.byBrand ?? []).map((b) => ({
                key: b.slug,
                label: b.name,
                count: b.count,
              }))}
              active={brand}
              onSelect={(value) => setFilter('brand', value)}
            />
          ) : null}
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <PenLine size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
            <p className="text-sm text-ink-700">
              {brand || category ? 'Nothing matches this filter.' : 'No corrections yet.'}
            </p>
            <p className="mx-auto mt-2 max-w-[540px] text-sm text-ink-400">
              {brand || category
                ? 'Clear a filter to see the rest.'
                : 'A correction is recorded whenever somebody edits a draft in the ticket workspace and sends the edited version. Send a few edited replies and this page becomes the record of what the agent keeps getting wrong.'}
            </p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th aria-label="Expand" className="w-8" />
                <Th>Ticket</Th>
                <Th>Brand</Th>
                <Th>Intent</Th>
                <Th>Reason</Th>
                <Th>Edited by</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <CorrectionRow key={row.id} row={row} />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function FilterRow({
  label,
  options,
  active,
  onSelect,
}: {
  label: string;
  options: Array<{ key: string; label: string; count: number | undefined }>;
  active: string | null;
  onSelect: (value: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-ink-500">{label}</span>
      {options.map((option) => {
        const selected = active === option.key;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(selected ? null : option.key)}
            className={cn(
              'rounded-sm px-2 py-0.5 text-2xs font-semibold transition-colors duration-fast',
              selected
                ? 'bg-accent-50 text-accent-700 shadow-hairline'
                : 'bg-surface-sunken text-ink-500 shadow-hairline hover:text-ink-900',
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span className={cn('tabular ml-1', selected ? 'text-accent-700' : 'text-ink-400')}>
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function ClusterRow({ cluster }: { cluster: Cluster }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [proposeError, setProposeError] = useState<string | null>(null);
  const Chevron = open ? ChevronDown : ChevronRight;

  const propose = useMutation({
    mutationFn: () =>
      apiPost<{ id: string; alreadyExists: boolean }>('/api/proposals', {
        clusterKey: cluster.clusterKey,
      }),
    // Either way there is now a proposal for this cluster; the review bench
    // is where the reader goes next.
    onSuccess: () => navigate('/proposals'),
    onError: (error: unknown) =>
      setProposeError(error instanceof ApiError ? error.message : 'The drafter did not run.'),
  });

  return (
    <>
      <tr
        onClick={() => setOpen((current) => !current)}
        className="cursor-pointer transition-colors duration-fast hover:bg-surface-sunken"
      >
        <Td className="w-8 pr-0">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide the example' : 'Show an example'}
            className="text-ink-400"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            <Chevron size={14} strokeWidth={1.75} aria-hidden />
          </button>
        </Td>
        <Td className="text-sm text-ink-900">
          {cluster.failureMode ? cluster.failureMode.replace(/_/g, ' ') : '—'}
        </Td>
        <Td className="text-xs text-ink-500">{cluster.brand}</Td>
        <Td className="text-xs text-ink-500">{intentLabel(cluster.intent)}</Td>
        <Td className="tabular text-right font-semibold text-ink-900">{cluster.count}</Td>
        <Td className="text-xs text-ink-500">{formatRelative(cluster.lastSeen)}</Td>
      </tr>
      {open ? (
        <tr>
          <Td colSpan={6} className="bg-surface-sunken">
            <p className="mb-2 text-2xs text-ink-400">
              Newest example, from{' '}
              <Link
                to={`/tickets/${cluster.example.ticketId}`}
                className="font-mono text-accent-700 hover:underline"
              >
                #{cluster.example.ticketRef}
              </Link>
            </p>
            <EditDiff draft={cluster.example.draft} final={cluster.example.final} />
            <div className="mt-3 flex items-center gap-3">
              <Button size="sm" disabled={propose.isPending} onClick={() => propose.mutate()}>
                <Lightbulb size={13} strokeWidth={1.75} aria-hidden />
                {propose.isPending ? 'Drafting…' : 'Propose a fix'}
              </Button>
              {proposeError ? <span className="text-xs text-stop-700">{proposeError}</span> : null}
            </div>
          </Td>
        </tr>
      ) : null}
    </>
  );
}

function CorrectionRow({ row }: { row: Correction }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <>
      <tr
        onClick={() => setOpen((current) => !current)}
        className="cursor-pointer transition-colors duration-fast hover:bg-surface-sunken"
      >
        <Td className="w-8 pr-0">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide the edit' : 'Show the edit'}
            className="text-ink-400"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            <Chevron size={14} strokeWidth={1.75} aria-hidden />
          </button>
        </Td>
        <Td>
          <Link
            to={`/tickets/${row.ticketId}`}
            onClick={(event) => event.stopPropagation()}
            className="font-mono text-xs text-accent-700 hover:underline"
          >
            #{row.ticketRef}
          </Link>
        </Td>
        <Td className="text-xs text-ink-500">{row.brand}</Td>
        <Td className="text-xs text-ink-500">{intentLabel(row.intent)}</Td>
        <Td>
          <Badge tone={row.humanCategory ? (CATEGORY_TONE[row.humanCategory] ?? 'neutral') : 'neutral'}>
            {categoryLabel(row.humanCategory)}
          </Badge>
          <div className="mt-0.5 text-2xs text-ink-400">
            {row.failureMode ? row.failureMode.replace(/_/g, ' ') : 'not clustered yet'}
          </div>
        </Td>
        <Td className="text-xs text-ink-500">{row.editedBy ?? '—'}</Td>
        <Td className="text-xs text-ink-500">{formatRelative(row.createdAt)}</Td>
      </tr>
      {open ? (
        <tr>
          <Td colSpan={7} className="bg-surface-sunken">
            <EditDiff draft={row.draft} final={row.final} />
          </Td>
        </tr>
      ) : null}
    </>
  );
}

/** The stored pair, rendered exactly as the editor saw it while typing. */
function EditDiff({ draft, final }: { draft: string; final: string }) {
  const tokens = useMemo(() => diffWords(draft, final), [draft, final]);

  return (
    <div className="max-w-[760px] whitespace-pre-wrap rounded border border-line bg-surface px-3 py-2.5 text-sm leading-relaxed">
      {tokens.map((token, index) => (
        <span
          key={index}
          className={cn(
            token.kind === 'del' && 'bg-stop-100 text-stop-700 line-through',
            token.kind === 'add' && 'bg-ok-100 text-ok-700',
          )}
        >
          {token.text}
        </span>
      ))}
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg bg-surface px-4 py-3 shadow-hairline">
      <div className="eyebrow">{label}</div>
      <div className="tabular mt-1 text-2xl font-semibold text-ink-900">{value}</div>
      <div className="text-2xs text-ink-400">{note}</div>
    </div>
  );
}
